"use client";

import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import {
  PageAnniversarySchema,
  PageAnniversaryPricingRow,
} from "@/sanity/pages/PageAnniversaryTemplate";
import { CTAButton } from "@/shadcn/ui/button";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";

export default function PricingTableSection({
  data,
}: {
  data: PageAnniversarySchema;
}) {
  if (!data.pricingRows?.length && !data.pricingTitle) return null;

  const sortedRows = [...(data.pricingRows ?? [])].sort(
    (a, b) => (b.highlighted ? 1 : 0) - (a.highlighted ? 1 : 0),
  );

  return (
    <section className="w-full bg-white py-[7rem] lg:py-[17.5rem]">
      <div className="mx-auto w-[min(141.5rem,100%)] px-[3rem] lg:px-0 flex flex-col gap-[5rem] items-center">
        {/* Header */}
        <div className="flex flex-col gap-[3.5rem] items-center w-full">
          <div className="flex flex-col items-center gap-[1.25rem] text-center max-w-[115.5rem]">
            {data.pricingChip && (
              <p className="mb-0 font-blacker italic text-[1.875rem] leading-[1.5] text-black">
                {data.pricingChip}
              </p>
            )}
            {data.pricingTitle && (
              <div className="richtext richtext-center prose-headings:font-semibold max-sm:prose-headings:text-[5rem] lg:prose-headings:text-[8rem] max-sm:prose-headings:tracking-[-0.25rem] prose-h2:mb-[2rem]">
                <PortableText
                  value={data.pricingTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>
        </div>

        {/* Desktop pricing table */}
        {!!data.pricingRows?.length && (
          <div className="hidden lg:block w-full overflow-x-auto">
            <div className="min-w-[100rem] flex flex-col overflow-hidden">
              {/* Column headers */}
              <div className="bg-[#f2f2f2] flex gap-[8rem] items-center px-[4rem] py-[1rem]">
                <p className="mb-0 font-geist font-medium text-[1.75rem] leading-[1.33] text-black/40 uppercase w-[25rem] shrink-0">
                  Paket
                </p>
                <p className="mb-0 font-geist font-medium text-[1.75rem] leading-[1.33] text-black/40 uppercase flex-1">
                  Rabatt
                </p>
                <p className="mb-0 font-geist font-medium text-[1.75rem] leading-[1.33] text-black/40 uppercase flex-1">
                  Gesamt
                </p>
                <p className="mb-0 font-geist font-medium text-[1.75rem] leading-[1.33] text-black/40 uppercase flex-1">
                  Preis / Account
                </p>
                <div className="w-[13.25rem] shrink-0" />
              </div>

              {/* Rows */}
              {data.pricingRows.map((row: PageAnniversaryPricingRow, i) => (
                <div key={row._key ?? i}>
                  {i > 0 && <div className="h-px bg-black/10" />}
                  <div
                    className={`relative flex gap-[8rem] items-center p-[4rem] ${
                      row.highlighted
                        ? "bg-[rgba(9,107,81,0.1)]"
                        : "bg-[#f7f7f7]"
                    }`}
                  >
                    {row.highlighted && (
                      <div className="absolute left-0 top-0 bottom-0 w-[0.5rem] bg-[#228e71]" />
                    )}

                    {/* Package */}
                    <div className="flex flex-col gap-[1rem] w-[25rem] shrink-0">
                      <p
                        className={`mb-0 font-geist font-medium text-[2.25rem] leading-[1.33] uppercase ${
                          row.highlighted ? "text-[#096b51]" : "text-black/90"
                        }`}
                      >
                        {row.packageName}
                      </p>
                      {row.packageCapital && (
                        <p
                          className={`mb-0 font-geist font-medium text-[1.75rem] leading-[1.33] ${
                            row.highlighted ? "text-[#096b51]" : "text-black/50"
                          }`}
                        >
                          {row.packageCapital}
                        </p>
                      )}
                    </div>

                    {/* Discount */}
                    <p
                      className={`mb-0 flex-1 font-spaceMono font-semibold text-[3.25rem] leading-[1.33] tracking-[-0.13rem] uppercase ${
                        row.highlighted ? "text-[#096b51]" : "text-[#003828]"
                      }`}
                    >
                      {row.discount}
                    </p>

                    {/* Total */}
                    <div className="flex flex-col flex-1">
                      <p
                        className={`mb-0 font-geist font-medium text-[2.5rem] leading-[1.33] uppercase ${
                          row.highlighted ? "text-[#096b51]" : "text-black/90"
                        }`}
                      >
                        {row.totalPrice}
                      </p>
                      {row.originalPrice && (
                        <p className="mb-0 font-geist font-medium text-[1.75rem] leading-[1.33] text-black/50 line-through">
                          {row.originalPrice}
                        </p>
                      )}
                    </div>

                    {/* Price per account */}
                    <p
                      className={`mb-0 flex-1 font-spaceMono font-semibold text-[3.25rem] leading-[1.33] tracking-[-0.13rem] uppercase ${
                        row.highlighted ? "text-[#096b51]" : "text-black"
                      }`}
                    >
                      {row.pricePerAccount}
                    </p>

                    {/* CTA */}
                    {row.cta && (
                      <div className="shrink-0">
                        <CTAButton data={row.cta} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mobile carousel */}
        {!!data.pricingRows?.length && (
          <div className="lg:hidden w-full">
            <Carousel opts={{ align: "start" }} className="w-full">
              <CarouselContent className="pl-0">
                {sortedRows.map((row: PageAnniversaryPricingRow, i) => (
                  <CarouselItem
                    key={row._key ?? i}
                    className="basis-[39.25rem]"
                  >
                    <div
                      className={`relative flex flex-col gap-[3rem] p-[3rem] h-full ${
                        row.highlighted
                          ? "bg-[rgba(9,107,81,0.1)]"
                          : "bg-[#f7f7f7]"
                      }`}
                    >
                      {/* Top accent bar */}
                      {row.highlighted && (
                        <div className="absolute top-0 left-0 right-0 h-[0.5rem] bg-[#228e71]" />
                      )}

                      {/* Package name + capital */}
                      <div className="flex flex-col gap-[1rem]">
                        <p
                          className={`mb-0 font-geist font-medium text-[2.25rem] leading-[1.33] uppercase ${
                            row.highlighted ? "text-[#096b51]" : "text-black/90"
                          }`}
                        >
                          {row.packageName}
                        </p>
                        {row.packageCapital && (
                          <p
                            className={`mb-0 font-geist font-medium text-[1.75rem] leading-[1.33] ${
                              row.highlighted
                                ? "text-[#096b51]"
                                : "text-black/50"
                            }`}
                          >
                            {row.packageCapital}
                          </p>
                        )}
                      </div>

                      {/* Stat rows */}
                      <div className="flex flex-col gap-[1.5rem]">
                        {/* Rabatt */}
                        <div className="flex items-center justify-between">
                          <p className="mb-0 font-geist font-medium text-[1.75rem] leading-[1.33] text-black/40 uppercase">
                            Rabatt
                          </p>
                          <p
                            className={`mb-0 font-spaceMono font-semibold text-[3rem] leading-[1.33] tracking-[-0.12rem] uppercase ${
                              row.highlighted
                                ? "text-[#096b51]"
                                : "text-[#003828]"
                            }`}
                          >
                            {row.discount}
                          </p>
                        </div>
                        <div className="h-px bg-black/10" />

                        {/* Gesamt */}
                        <div className="flex items-center justify-between">
                          <p className="mb-0 font-geist font-medium text-[1.75rem] leading-[1.33] text-black/40 uppercase">
                            Gesamt
                          </p>
                          <div className="flex flex-col items-end">
                            <p
                              className={`mb-0 font-geist font-medium text-[3rem] leading-[1.33] uppercase ${
                                row.highlighted
                                  ? "text-[#096b51]"
                                  : "text-black/90"
                              }`}
                            >
                              {row.totalPrice}
                            </p>
                            {row.originalPrice && (
                              <p className="mb-0 font-geist font-medium text-[1.75rem] leading-[1.33] text-black/50 line-through">
                                {row.originalPrice}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="h-px bg-black/10" />

                        {/* Preis / Account */}
                        <div className="flex items-center justify-between">
                          <p className="mb-0 font-geist font-medium text-[1.75rem] leading-[1.33] text-black/40 uppercase">
                            Preis / Account
                          </p>
                          <p
                            className={`mb-0 font-spaceMono font-semibold text-[3rem] leading-[1.33] tracking-[-0.12rem] uppercase ${
                              row.highlighted ? "text-[#096b51]" : "text-black"
                            }`}
                          >
                            {row.pricePerAccount}
                          </p>
                        </div>
                      </div>

                      {/* CTA */}
                      {row.cta && (
                        <div className="w-full [&>*]:w-full [&>*]:justify-center">
                          <CTAButton data={row.cta} />
                        </div>
                      )}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        )}

        {/* Footer text */}
        {data.pricingFooterText && (
          <div className="richtext richtext-center prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-black/70 max-w-[98.75rem]">
            <PortableText
              value={data.pricingFooterText}
              components={richTextComponent}
            />
          </div>
        )}
      </div>
    </section>
  );
}
